import deliveryService from '../services/delivery.service';
import { CustomField } from '../types/customField.types';

export type CityFieldMappingCity = {
  id: string;
  name: string;
  ref?: string;
  delivered_price?: number;
  returned_price?: number;
  refused_price?: number;
};

export type CityFieldMappingEntry = {
  cities: CityFieldMappingCity[];
  customFieldKey: string;
};

export type CityFieldMappings = Record<string, CityFieldMappingEntry>;

type RawCity = {
  city_id?: string | number;
  id?: string | number;
  city_name?: string;
  name?: string;
  city_ref?: string;
  ref?: string;
  delivered_price?: number | string | null;
  returned_price?: number | string | null;
  refused_price?: number | string | null;
};

export const normalizeCity = (city: RawCity, index: number): CityFieldMappingCity => ({
  id: String(city.city_id ?? city.id ?? index),
  name: (city.city_name || city.name || '').trim(),
  ref: (city.city_ref || city.ref || '').trim() || undefined,
  delivered_price: city.delivered_price != null ? Number(city.delivered_price) : undefined,
  returned_price: city.returned_price != null ? Number(city.returned_price) : undefined,
  refused_price: city.refused_price != null ? Number(city.refused_price) : undefined
});

const dedupeCities = (cities: CityFieldMappingCity[]): CityFieldMappingCity[] => {
  const seen = new Set<string>();
  const result: CityFieldMappingCity[] = [];
  for (const city of cities) {
    if (!city.name) continue;
    const key = `${city.id}:${city.name.toLowerCase()}`;
    if (seen.has(key)) continue;
    seen.add(key);
    result.push(city);
  }
  return result.sort((a, b) => a.name.localeCompare(b.name, 'fr'));
};

/** Villes à afficher pour un champ (clé du champ ou pool partagé). */
export function getCitiesForField(
  mappings: CityFieldMappings,
  field: Pick<CustomField, 'key' | 'type'>
): CityFieldMappingCity[] {
  const direct = mappings[field.key]?.cities;
  if (direct?.length) return direct;

  if (field.type === 'delivery_city') {
    const pooled = Object.values(mappings).find((entry) => entry.cities.length > 0);
    if (pooled) return pooled.cities;
  }

  return [];
}

export function cityFieldMappingsHasCities(mappings: CityFieldMappings): boolean {
  return Object.values(mappings).some((entry) => entry.cities.length > 0);
}

const citiesVersionStorageKey = (storeId: number, integrationKey: string) =>
  `zc_cities_version_${storeId}_${integrationKey}`;

const readStoredCitiesVersion = (storeId: number, integrationKey: string): string | null => {
  try {
    return sessionStorage.getItem(citiesVersionStorageKey(storeId, integrationKey));
  } catch {
    return null;
  }
};

const writeStoredCitiesVersion = (storeId: number, integrationKey: string, version: string) => {
  try {
    sessionStorage.setItem(citiesVersionStorageKey(storeId, integrationKey), version);
  } catch {
    /* ignore */
  }
};

const applyCitiesVersionBump = (
  storeId: number,
  integrationKey: string,
  citiesVersion: string | null | undefined,
  citiesByIntegrationKey: Map<string, CityFieldMappingCity[]>
) => {
  if (!integrationKey || !citiesVersion) return;
  const prev = readStoredCitiesVersion(storeId, integrationKey);
  if (prev && prev !== citiesVersion) {
    citiesByIntegrationKey.delete(integrationKey);
    deliveryService.invalidateDeliveryCitiesCache(storeId, integrationKey);
  }
  writeStoredCitiesVersion(storeId, integrationKey, citiesVersion);
};

export async function loadOrderCityFieldMappings(
  storeId: number,
  customFields: CustomField[]
): Promise<CityFieldMappings> {
  const mappings: CityFieldMappings = {};
  const citiesByIntegrationKey = new Map<string, CityFieldMappingCity[]>();

  const loadCitiesForIntegrationKey = async (integrationKey: string): Promise<CityFieldMappingCity[]> => {
    if (citiesByIntegrationKey.has(integrationKey)) {
      return citiesByIntegrationKey.get(integrationKey)!;
    }
    try {
      const citiesData = await deliveryService.getCities(storeId, integrationKey);
      if (citiesData.cities_version) {
        applyCitiesVersionBump(storeId, integrationKey, citiesData.cities_version, citiesByIntegrationKey);
      }
      const cities = dedupeCities(
        ((citiesData.cities || []) as RawCity[]).map(normalizeCity)
      );
      citiesByIntegrationKey.set(integrationKey, cities);
      return cities;
    } catch (error) {
      console.error(`[CityFieldMappings] Failed to load cities for ${integrationKey}:`, error);
      citiesByIntegrationKey.set(integrationKey, []);
      return [];
    }
  };

  try {
    const cityFieldMappingData = await deliveryService.getCityFieldMapping(storeId);

    for (const mapping of cityFieldMappingData?.mappings || []) {
      const integrationKey = mapping.integration_key as string | undefined;
      if (integrationKey && (mapping as { cities_version?: string }).cities_version) {
        applyCitiesVersionBump(
          storeId,
          integrationKey,
          (mapping as { cities_version?: string }).cities_version,
          citiesByIntegrationKey
        );
      }
      let cities = dedupeCities(((mapping.cities || []) as RawCity[]).map(normalizeCity));

      if (cities.length === 0 && integrationKey) {
        cities = await loadCitiesForIntegrationKey(integrationKey);
      }

      if (integrationKey && cities.length > 0) {
        citiesByIntegrationKey.set(integrationKey, cities);
      }

      if (!mapping.custom_field_key || cities.length === 0) continue;

      const field = customFields.find((item) => item.key === mapping.custom_field_key);
      if (!field) continue;

      mappings[field.key] = {
        cities,
        customFieldKey: field.key
      };
    }
  } catch (error) {
    console.error('Failed to load city field mappings from delivery service:', error);
  }

  let integrations: Array<{ id: number; key: string }> = [];
  try {
    integrations = await deliveryService.getStoreDeliveryIntegrations(storeId);
  } catch (error) {
    console.error('[CityFieldMappings] Failed to load delivery integrations:', error);
  }

  for (const field of customFields.filter((item) => item.type === 'delivery_city')) {
    if (mappings[field.key]?.cities?.length) continue;

    let integration = field.delivery_integration_id
      ? integrations.find((int) => int.id === field.delivery_integration_id)
      : undefined;

    if (!integration?.key && integrations.length === 1) {
      integration = integrations[0];
    }

    if (!integration?.key) continue;

    const cities = await loadCitiesForIntegrationKey(integration.key);
    if (cities.length > 0) {
      mappings[field.key] = {
        cities,
        customFieldKey: field.key
      };
    }
  }

  const sharedPool = dedupeCities(
    Array.from(citiesByIntegrationKey.values()).flat()
  );

  if (sharedPool.length > 0) {
    for (const field of customFields.filter((item) => item.type === 'delivery_city')) {
      if (!mappings[field.key]?.cities?.length) {
        mappings[field.key] = {
          cities: sharedPool,
          customFieldKey: field.key
        };
      }
    }
  }

  return mappings;
}
